import React, { useEffect, useRef, useState } from "react";
import { ColorV2 } from "config/colors";
import { CustomIcon } from "components/icon/customIcon.component";
import SimflexityLogo from "images/icons/svg/v2/simflexity-logo.svg";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "components/buttons/buttons.styled";
import { useAuthStore } from "stores/authStore/authStore";
import { CompanyRole } from "model/User";
import {
  Container,
  IconWrap,
  ItemLink,
  Items,
  ItemsTrack,
  Label,
  Logo,
  MeasureItem,
  MeasureTrack,
} from "./navbarMenu.styles";

export interface MenuItem {
  value: string;
  label: string;
  iconName: string;
  href: string;
  badgeCount?: number;
}

interface MenuProps {
  items: MenuItem[];
  activeValue: string;
  logoSrc?: string;
  onItemClick?: (
    item: MenuItem,
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => void;
}

interface NavbarMenuItemProps {
  item: MenuItem;
  isActive: boolean;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  fillAvailableSpace: boolean;
  showLabel: boolean;
}

const NavbarMenuItem = ({
  item,
  isActive,
  fillAvailableSpace,
  onClick,
  showLabel,
}: NavbarMenuItemProps): React.ReactElement => {
  const [isHighlighted, setIsHighlighted] = useState(false);
  const iconColor = isHighlighted
    ? ColorV2.Blue3
    : isActive
      ? ColorV2.Blue1
      : ColorV2.Blue5;
  const isExpanded = !showLabel && isHighlighted;

  return (
    <ItemLink
      to={item.href}
      aria-current={isActive ? "page" : undefined}
      aria-label={item.label}
      $isExpanded={isExpanded}
      $fillAvailableSpace={fillAvailableSpace}
      $isActive={isActive}
      $showLabel={showLabel}
      onClick={onClick}
      onFocus={() => setIsHighlighted(true)}
      onBlur={() => setIsHighlighted(false)}
      onMouseEnter={() => setIsHighlighted(true)}
      onMouseLeave={() => setIsHighlighted(false)}
    >
      <IconWrap>
        <CustomIcon name={item.iconName} size="24px" color={iconColor} />
        {typeof item.badgeCount === "number" && item.badgeCount > 0 && (
          <Badge>{item.badgeCount}</Badge>
        )}
      </IconWrap>
      <Label $isExpanded={isExpanded} $showLabel={showLabel}>
        {item.label}
      </Label>
    </ItemLink>
  );
};

export const NavbarMenu = ({
  items,
  activeValue,
  logoSrc = SimflexityLogo,
  onItemClick,
}: MenuProps): React.ReactElement => {
  const { t } = useTranslation();
  const [authState] = useAuthStore();
  const itemsRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [showLabels, setShowLabels] = useState(true);

  useEffect((): (() => void) | void => {
    if (typeof ResizeObserver === "undefined") return undefined;

    const itemsElement = itemsRef.current;
    const measureElement = measureRef.current;

    if (itemsElement == null || measureElement == null) return undefined;

    const updateLabelVisibility = (): void => {
      const availableWidth = itemsElement.clientWidth;
      const requiredWidth = measureElement.scrollWidth;
      setShowLabels(requiredWidth <= availableWidth);
    };

    updateLabelVisibility();

    const resizeObserver = new ResizeObserver(() => {
      updateLabelVisibility();
    });

    resizeObserver.observe(itemsElement);
    resizeObserver.observe(measureElement);

    return (): void => {
      resizeObserver.disconnect();
    };
  }, [items]);

  return (
    <Container role="navigation" aria-label={t("DesignComponents.MainMenu")}>
      {authState.user?.role !== CompanyRole.COMPANY_TIMESTAMP && (
        <Link to="/company/schedule">
          <Logo src={logoSrc} alt="Simflexity logo" />
        </Link>
      )}
      {authState.user?.role === CompanyRole.COMPANY_TIMESTAMP && (
        <Link to="/company/stampclock">
          <Logo src={logoSrc} alt="Simflexity logo" />
        </Link>
      )}
      <Items ref={itemsRef} $showLabels={showLabels}>
        <ItemsTrack $showLabels={showLabels}>
          {items.map((item) => (
            <NavbarMenuItem
              key={item.value}
              fillAvailableSpace={showLabels}
              item={item}
              isActive={item.value === activeValue}
              onClick={(event) => onItemClick?.(item, event)}
              showLabel={showLabels}
            />
          ))}
        </ItemsTrack>
      </Items>
      <MeasureTrack ref={measureRef} aria-hidden>
        {items.map((item) => (
          <MeasureItem key={item.value}>
            <CustomIcon
              name={item.iconName}
              size="24px"
              color={ColorV2.Blue5}
            />
            <span>{item.label}</span>
          </MeasureItem>
        ))}
      </MeasureTrack>
    </Container>
  );
};
